import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const DISMISSED_KEY = 'endless-email-capture-dismissed';
const SUBSCRIBED_KEY = 'endless-email-capture-subscribed';
const SUBMITTING_KEY = 'endless-email-capture-submitting';
const MAX_CONFLICT_RETRIES = 60;

class EmailCaptureModal extends DialogComponent {
  /** @type {number | undefined} */
  #openTimer;

  #conflictRetries = 0;

  #submittedSuccessfully = false;

  connectedCallback() {
    super.connectedCallback();

    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#handleSubmit);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);

    if (this.#handleFormResult()) return;
    if (!this.#isEligible()) return;

    const delay = Math.max(0, Number(this.dataset.delaySeconds) || 0) * 1000;
    this.#openTimer = window.setTimeout(this.#attemptOpen, delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#openTimer);

    const form = this.querySelector('form');
    form?.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
  }

  #handleSubmit = () => {
    this.#writeStorage(sessionStorage, SUBMITTING_KEY, 'true');
  };

  #handleClose = () => {
    if (!this.#submittedSuccessfully) this.#rememberDismissal();
  };

  /**
   * Restores the modal after Shopify returns a customer-form result.
   * The session marker prevents another newsletter form on the page from
   * being mistaken for this popup's submission.
   * @returns {boolean}
   */
  #handleFormResult() {
    const result = this.querySelector('[data-email-capture-result]:not([hidden])');
    if (!(result instanceof HTMLElement)) return false;

    const submittedHere =
      this.#readStorage(sessionStorage, SUBMITTING_KEY) === 'true' || window.location.hash === '#EmailCaptureForm';

    this.#removeStorage(sessionStorage, SUBMITTING_KEY);

    if (!submittedHere) {
      this.#resetForeignFormResult();
      return false;
    }

    const state = result.dataset.emailCaptureResult;
    if (state === 'success') {
      this.#submittedSuccessfully = true;
      this.#writeStorage(localStorage, SUBSCRIBED_KEY, 'true');
      this.#removeStorage(localStorage, DISMISSED_KEY);
    }

    queueMicrotask(() => {
      this.#attemptOpen(true);
      window.setTimeout(() => result.focus(), 100);
    });

    return true;
  }

  #resetForeignFormResult() {
    const fields = this.querySelector('[data-email-capture-fields]');
    if (fields instanceof HTMLElement) fields.hidden = false;

    this.querySelectorAll('[data-email-capture-result]').forEach((result) => {
      if (result instanceof HTMLElement) result.hidden = true;
    });

    const input = this.querySelector('input[type="email"]');
    if (input instanceof HTMLInputElement) {
      input.value = '';
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  #isEligible() {
    if (window.Shopify?.designMode) return false;
    if (this.dataset.customerSubscribed === 'true') return false;
    if (this.#readStorage(localStorage, SUBSCRIBED_KEY) === 'true') return false;

    const template = this.dataset.template || '';
    if (['cart', 'password', 'gift_card'].some((excluded) => template.startsWith(excluded))) return false;
    if (template.startsWith('customers/')) return false;

    const frequency = this.dataset.frequency || '30';
    if (frequency === 'session') {
      return this.#readStorage(sessionStorage, DISMISSED_KEY) !== 'true';
    }

    const dismissedAt = Number(this.#readStorage(localStorage, DISMISSED_KEY));
    if (!dismissedAt) return true;
    if (frequency === 'never') return false;

    const days = Number(frequency);
    if (!Number.isFinite(days) || days <= 0) return true;

    return Date.now() - dismissedAt >= days * 24 * 60 * 60 * 1000;
  }

  #attemptOpen = (force = false) => {
    if (!force && !this.#isEligible()) return;

    if (document.visibilityState !== 'visible' || document.querySelector('dialog[open]')) {
      if (this.#conflictRetries >= MAX_CONFLICT_RETRIES) return;
      this.#conflictRetries += 1;
      this.#openTimer = window.setTimeout(() => this.#attemptOpen(force), 1000);
      return;
    }

    this.#conflictRetries = 0;
    this.showDialog();

    // Start cooldown as soon as the popup is shown so navigating away
    // (without closing) still suppresses future opens for the period.
    if (!this.#submittedSuccessfully) this.#rememberShown();
  };

  /**
   * Records that the popup was shown (or dismissed). Uses the same storage
   * keys as the frequency/eligibility checks.
   */
  #rememberShown() {
    const frequency = this.dataset.frequency || '30';

    if (frequency === 'session') {
      this.#writeStorage(sessionStorage, DISMISSED_KEY, 'true');
      return;
    }

    this.#writeStorage(localStorage, DISMISSED_KEY, String(Date.now()));
  }

  #rememberDismissal() {
    this.#rememberShown();
  }

  /**
   * @param {Storage} storage
   * @param {string} key
   * @returns {string | null}
   */
  #readStorage(storage, key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {Storage} storage
   * @param {string} key
   * @param {string} value
   */
  #writeStorage(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch {
      // Storage can be unavailable in private or restricted browser contexts.
    }
  }

  /**
   * @param {Storage} storage
   * @param {string} key
   */
  #removeStorage(storage, key) {
    try {
      storage.removeItem(key);
    } catch {
      // Storage can be unavailable in private or restricted browser contexts.
    }
  }
}

if (!customElements.get('email-capture-modal')) {
  customElements.define('email-capture-modal', EmailCaptureModal);
}
